import React, { useState, useEffect } from 'react';
import { CheckCircle, X, Eye } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api/v1`;

interface Submission {
  id: string;
  title: string;
  description: string;
  status: string;
  submitted_by_user_name: string;
  asset: any;
  assets: any[];
  packaged_assets: any[];
  caption: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  'APPROVAL_1_PENDING': 'bg-yellow-100 text-yellow-800',
  'APPROVAL_2_PENDING': 'bg-yellow-100 text-yellow-800',
};

const ApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      // Only fetch Step 2 (Packaged) approvals - APPROVAL_2_PENDING
      const response = await axios.get(`${API}/submissions`, {
        params: { status: 'APPROVAL_2_PENDING', page_size: 50 }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;
    
    try {
      setProcessing(true);
      // Always Step 2 approval for packaged assets
      await axios.post(`${API}/submissions/${selectedSubmission.id}/approve`, {
        step: 2,
        comment: approveComment || undefined
      });
      setShowApproveModal(false);
      setSelectedSubmission(null);
      setApproveComment('');
      await fetchApprovals();
      alert('Submission approved successfully!');
    } catch (error: any) {
      console.error('Failed to approve:', error);
      alert(error.response?.data?.detail || 'Failed to approve submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission || !rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    try {
      setProcessing(true);
      // Always Step 2 rejection for packaged assets
      await axios.post(`${API}/submissions/${selectedSubmission.id}/reject`, {
        step: 2,
        reason: rejectReason
      });
      setShowRejectModal(false);
      setSelectedSubmission(null);
      setRejectReason('');
      await fetchApprovals();
      alert('Submission rejected successfully!');
    } catch (error: any) {
      console.error('Failed to reject:', error);
      alert(error.response?.data?.detail || 'Failed to reject submission');
    } finally {
      setProcessing(false);
    }
  };

  const openApproveModal = (submission: Submission) => {
    setSelectedSubmission(submission);
    setShowApproveModal(true);
  };

  const openRejectModal = (submission: Submission) => {
    setSelectedSubmission(submission);
    setShowRejectModal(true);
  };

  // Check if user can approve/reject (Marketing Head only)
  const canApproveReject = user?.roles?.includes('Marketing Head');

  return (
    <div data-testid="approvals-page">
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 lg:p-6 mb-4 lg:mb-6 border border-gray-200">
        <div className="mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
            Packaged Asset Approvals
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Review and approve packaged submissions from Creative Agency</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600">No pending packaged asset approvals</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Title</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Submitted By</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Packaged Asset</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Caption</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => {
                    const firstPackagedAsset = submission.packaged_assets && submission.packaged_assets.length > 0 ? submission.packaged_assets[0] : null;
                    const firstAsset = submission.assets && submission.assets.length > 0 ? submission.assets[0] : submission.asset;
                    
                    return (
                      <tr key={submission.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <button
                            onClick={() => navigate(`/submissions/${submission.id}`)}
                            className="text-left"
                          >
                            <p className="font-medium text-gray-900 hover:text-blue-600 underline cursor-pointer">{submission.title}</p>
                            <p className="text-sm text-gray-600 truncate max-w-xs">{submission.description}</p>
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-700">{submission.submitted_by_user_name}</td>
                        <td className="py-3 px-4 text-gray-700 text-sm">
                          {new Date(submission.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[submission.status]}`}>
                            {submission.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {firstPackagedAsset ? (
                            <div className="flex items-center gap-2">
                              {firstPackagedAsset.type === 'image' && (
                                <span className="text-sm text-gray-600">📷 Image</span>
                              )}
                              {firstPackagedAsset.type === 'video' && (
                                <span className="text-sm text-gray-600">🎥 Video</span>
                              )}
                              <span className="text-xs text-gray-500">{firstPackagedAsset.original_filename}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No asset</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {submission.caption ? (
                            <div className="max-w-xs">
                              <p className="text-sm text-gray-700 line-clamp-2">{submission.caption}</p>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No caption</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/submissions/${submission.id}`)}
                              className="p-2 text-gray-600 hover:text-blue-600 transition"
                              title="View Details"
                              data-testid={`view-${submission.id}`}
                            >
                              <Eye size={18} />
                            </button>
                            {canApproveReject && (
                              <>
                                <button
                                  onClick={() => openApproveModal(submission)}
                                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition text-sm font-medium"
                                  data-testid={`approve-${submission.id}`}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => openRejectModal(submission)}
                                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-sm font-medium"
                                  data-testid={`reject-${submission.id}`}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {submissions.map((submission) => {
                const firstPackagedAsset = submission.packaged_assets && submission.packaged_assets.length > 0 ? submission.packaged_assets[0] : null;
                const firstAsset = submission.assets && submission.assets.length > 0 ? submission.assets[0] : submission.asset;
                
                return (
                  <div key={submission.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <h3 
                        onClick={() => navigate(`/submissions/${submission.id}`)}
                        className="font-medium text-gray-900 flex-1 pr-2 cursor-pointer hover:text-blue-600"
                      >
                        {submission.title}
                      </h3>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[submission.status]}`}>
                        {submission.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{submission.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                      <span>{submission.submitted_by_user_name}</span>
                      <span>{new Date(submission.created_at).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="mb-3 pb-3 border-b border-gray-200 space-y-2">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Packaged Asset:</p>
                        {firstPackagedAsset ? (
                          <div className="flex items-center gap-2 text-sm">
                            {firstPackagedAsset.type === 'image' && <span>📷 Image</span>}
                            {firstPackagedAsset.type === 'video' && <span>🎥 Video</span>}
                            <span className="text-xs text-gray-600 truncate">{firstPackagedAsset.original_filename}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No asset</span>
                        )}
                      </div>
                      {submission.caption && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Caption:</p>
                          <p className="text-sm text-gray-700 line-clamp-3">{submission.caption}</p>
                        </div>
                      )}
                    </div>
                    
                    {canApproveReject && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => openApproveModal(submission)}
                          className="flex-1 min-h-[44px] px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openRejectModal(submission)}
                          className="flex-1 min-h-[44px] px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedSubmission && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowApproveModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Approve Submission?</h3>
            <p className="text-gray-600 mb-1">
              <strong>{selectedSubmission.title}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              This will approve the submission and move it to the next stage.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment (Optional)
              </label>
              <textarea
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                rows={3}
                placeholder="Add any comments or notes about this approval..."
                data-testid="approve-comment-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowApproveModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                disabled={processing}
                data-testid="confirm-approve"
              >
                {processing ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedSubmission && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRejectModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Submission?</h3>
            <p className="text-gray-600 mb-1">
              <strong>{selectedSubmission.title}</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejection:
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-6"
              rows={4}
              placeholder="Explain why this submission is being rejected..."
              data-testid="reject-reason-input"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={processing || !rejectReason.trim()}
                data-testid="confirm-reject"
              >
                {processing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalsPage;